import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    ListHeader,
    // HeaderActionButton,
    SkeletonLoader,
    ListSkeleton,
    EmptyBox,
    HeaderListSkeleton,
} from '@europrocurement/flexy-components';
import {
    ColumnDatatable,
    FiltersDatatableList,
    StoreDatatable,
} from '@europrocurement/flexy-datatable';
import {
    Prescripteur,
    PrescripteursSelector,
    useTiersService,
    TiersAppDispatch,
} from '@europrocurement/l2d-domain';
import { useClickActions, useInitialLoadingState } from '@europrocurement/l2d-hooks';

import { /** createPrescriberIcon, */ FaOptionIcon } from '@europrocurement/l2d-icons';
import { faBuildings, faPerson } from '@fortawesome/sharp-regular-svg-icons';
import { Box, Typography } from '@mui/material';
import { PossibleTypes, useSearchBar } from '@europrocurement/l2d-modules';
import { useAppSelector } from '../../../../redux/types';
import { customizerSelector } from '../../../../redux/RootStore';
import { PrescripteursListingActions } from './PrescripteursListingActions';
// import { PRESCRIBER_NEW } from '../../constants/paths';

const PrescripteursListing: React.FunctionComponent = function () {
    const dispatch = useDispatch<TiersAppDispatch>();
    const navigate = useNavigate();
    const location = useLocation();

    const datasource = useAppSelector(PrescripteursSelector).main;
    const { xIdSociete } = useAppSelector(customizerSelector);
    const { getPrescripteurs } = useTiersService();
    const { setIsSearchBarPath, setSearchBarType } = useSearchBar();
    const { isInitialLoading, isInError } = useInitialLoadingState(datasource);
    const { navigateOnClick } = useClickActions({ navigate });

    // Chargement initial des données
    React.useEffect(() => {
        dispatch(getPrescripteurs({}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Activation du flag de recherche prescripteur
    React.useEffect(() => {
        setSearchBarType(PossibleTypes.Prescriber);
        setIsSearchBarPath(true);
        return () => {
            setSearchBarType(undefined);
            setIsSearchBarPath(false);
        };
    }, [setIsSearchBarPath, setSearchBarType]);

    const handleRowClick = React.useCallback(
        (_: React.MouseEvent<HTMLElement>, { id }: Prescripteur) => {
            navigateOnClick({ path: `${id}` });
        },
        [navigateOnClick],
    );

    const handleRowWheelClick = React.useCallback(
        (_: React.MouseEvent<HTMLElement>, { id }: Prescripteur) => {
            navigateOnClick({ path: `${location.pathname}/${id}`, openNewTab: true });
        },
        [location.pathname, navigateOnClick],
    );

    const columns: ColumnDatatable<Prescripteur>[] = React.useMemo(
        () => [
            {
                label: 'Statut',
                displayLabel: false,
                render: ({ personnePhysique, demo }) =>
                    personnePhysique ? (
                        <FaOptionIcon
                            icon={faPerson}
                            color={demo ? 'error' : 'primary'}
                        />
                    ) : (
                        <FaOptionIcon
                            icon={faBuildings}
                            color={demo ? 'error' : 'primary'}
                        />
                    ),
                isDisplayed: true,
                sortable: true,
                field: 'raisonSociale',
            },
            {
                label: 'Code Client',
                render: ({ relationsSocietes }) => (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {relationsSocietes.find((item) => item.societe?.id === xIdSociete)
                            ?.codeClient || ''}
                    </Typography>
                ),
                isDisplayed: true,
            },
            {
                label: 'Raison sociale',
                render: ({ raisonSociale }) => (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {raisonSociale}
                    </Typography>
                ),
                isDisplayed: true,
                sortable: true,
                field: 'raisonSociale',
            },
            {
                label: 'CP Ville',
                render: ({ adresses }) => {
                    if (!adresses || !adresses.length) return null;
                    const { ville, codePostal } = adresses[0];
                    return (
                        <Typography
                            variant="l2dtableCell"
                            color="text.gris70"
                        >
                            {`${codePostal} ${ville.toUpperCase()}`}
                        </Typography>
                    );
                },
                isDisplayed: true,
            },
            {
                label: 'Siret',
                render: ({ siret }: Prescripteur) => (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {siret}
                    </Typography>
                ),
                isDisplayed: false,
            },
            {
                label: 'Contact',
                render: ({ contacts }: Prescripteur) => {
                    if (!contacts || !contacts.length) return null;
                    return (
                        <Typography
                            variant="l2dtableCell"
                            color="text.gris70"
                        >{`${contacts[0].nomContact.toUpperCase()} ${contacts[0].prenomContact}`}</Typography>
                    );
                },
                isDisplayed: true,
            },
            {
                label: 'Actions',
                displayLabel: false,
                render: (item) => item.id && <PrescripteursListingActions itemId={item.id} />,
                isDisplayed: true,
            },
        ],
        [xIdSociete],
    );

    const filters: FiltersDatatableList = React.useMemo(
        () => [
            { label: 'Raison sociale', field: 'raisonSociale', type: 'text' },
            { label: 'Code client', field: 'relationsSocietes.codeClient', type: 'text' },
            { label: 'Nom du contact', field: 'contacts.nomContact', type: 'text' },
            { label: 'Prénom du contact', field: 'contacts.prenomContact', type: 'text' },
            { label: 'Email du contact', field: 'contacts.emailsContact.email', type: 'text' },
            {
                label: 'Téléphone du contact',
                field: 'contacts.telephonesContact.telephone',
                type: 'text',
            },
            { label: 'SIRET', field: 'siret', type: 'text' },
            { label: 'SIREN', field: 'siren', type: 'text' },
            { label: 'Nom du commercial', field: 'commerciaux.utilisateur.nom', type: 'text' },
            {
                label: 'Prénom du commercial',
                field: 'commerciaux.utilisateur.prenom',
                type: 'text',
            },
            { label: 'Formaliste', field: 'formaliste.nom', type: 'text' },
            { label: 'Adresse', field: 'adresses.codePostal', type: 'text' },
            { label: 'Demo', field: 'demo', type: 'boolean' },
            { label: 'Supprimé', field: 'deleted', type: 'boolean' },
        ],
        [],
    );

    const errorContent = React.useMemo(
        () => (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '80vh',
                    width: '100%',
                }}
            >
                <EmptyBox
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-evenly',
                        height: '25%',
                        width: '50%',
                    }}
                    text="Une erreur s'est produite lors de la récupération des données, échec du chargement des prescripteurs."
                    subText="Veuillez recharger la page. Si le problème persiste, contactez votre administrateur."
                />
            </Box>
        ),
        [],
    );

    return (
        <SkeletonLoader
            isLoading={isInitialLoading}
            skeleton={
                <>
                    <HeaderListSkeleton />
                    <ListSkeleton
                        showSearch
                        columns={6}
                    />
                </>
            }
            isInError={isInError}
            errorChildren={errorContent}
        >
            <ListHeader
                breadcrumbPath={[
                    { path: 'Prescripteurs', link: '/prescripteurs/liste' },
                    "Recherche d'un prescripteur",
                    'Liste des prescripteurs',
                ]}
                // actions={
                //     <HeaderActionButton
                //         label="Créer un prescripteur"
                //         icon={createPrescriberIcon}
                //         onClick={() => navigate(PRESCRIBER_NEW)}
                //         actionType="navigation"
                //     />
                // }
            />

            {datasource && (
                <StoreDatatable
                    dataSource={datasource}
                    columns={columns}
                    fetchData={getPrescripteurs}
                    filters={filters}
                    localStorageKey="FlexyStoreDatatable"
                    onClickRow={handleRowClick}
                    onWheelClickRow={handleRowWheelClick}
                    observers={[xIdSociete]}
                />
            )}
        </SkeletonLoader>
    );
};

export default PrescripteursListing;
